/**
 * Project Header Component
 * 
 * Unified header with project info and centralized actions
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Space,
  Typography,
  Tag,
  Dropdown,
  message,
  Modal,
  Progress,
  List,
  Spin,
  Badge,
  Tooltip,
} from 'antd'
import {
  LeftOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  DownOutlined,
  RobotOutlined,
  LinkOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwitcherOutlined,
  ReloadOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import api from '@/services/api'
import UnifiedParsingModal from '@/components/UnifiedParsingModal'
import { useParsingStore } from '@/stores/parsingStore'

const { Title, Text } = Typography

interface ProjectHeaderProps {
  projectId: number
  onSync?: () => void
  onGenerateConfig?: () => void
  onReanalyze?: () => void
}

interface Project {
  id: number
  crm_id: number
  project_name: string
  status: string
  parse_manual: boolean
  parse_phones: boolean
  parse_domains: boolean
}

interface Region {
  id: number
  name: string
}

interface CollectionProgress {
  status: 'idle' | 'running' | 'completed' | 'error'
  progress: number
  queries: string[]
  regions: Region[]
  queries_count: number
  regions_count: number
  message?: string
}

const statusColors: Record<string, string> = {
  active: 'success',
  test_active: 'processing',
  pause: 'warning',
  stop: 'default',
  test_stop: 'default',
}

const statusLabels: Record<string, string> = {
  active: 'Активен',
  test_active: 'Тест активен',
  pause: 'Пауза',
  stop: 'Остановлен',
  test_stop: 'Тест остановлен',
}

function ProjectHeader({ projectId, onSync, onGenerateConfig, onReanalyze }: ProjectHeaderProps) {
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(false)
  const [parsingModalVisible, setParsingModalVisible] = useState(false)
  const [collectionModalVisible, setCollectionModalVisible] = useState(false)
  const [collectionProgress, setCollectionProgress] = useState<CollectionProgress>({
    status: 'idle',
    progress: 0,
    queries: [],
    regions: [],
    queries_count: 0,
    regions_count: 0,
  })
  
  // * Get active parsing task for this project
  const activeTask = useParsingStore((state) => state.getActiveTask(projectId))
  const updateTaskProgress = useParsingStore((state) => state.updateTaskProgress)
  const updateTaskStatus = useParsingStore((state) => state.updateTaskStatus)

  useEffect(() => {
    fetchProject()
    // * Check for active parsing on mount
    checkActiveParsingStatus()
  }, [projectId])
  
  // * Poll active parsing status
  useEffect(() => {
    if (!activeTask || activeTask.status === 'completed' || activeTask.status === 'failed') {
      return
    }
    
    const interval = setInterval(() => {
      checkActiveParsingStatus()
    }, 5000) // * Poll every 5 seconds
    
    return () => clearInterval(interval)
  }, [activeTask, projectId])

  const fetchProject = async () => {
    try {
      setLoading(true)
      const data = await api.getProject(projectId)
      setProject(data)
    } catch (error: any) {
      console.error('Error loading project:', error)
      message.error('Ошибка загрузки проекта')
    } finally {
      setLoading(false)
    }
  }
  
  const checkActiveParsingStatus = async () => {
    try {
      const tasksData = await api.getParsingTasks()
      const tasksList = tasksData.tasks || []
      
      // * Find active task for this project
      const runningTask = tasksList.find((t: any) => 
        t.project_id === projectId && 
        (t.status === 'running' || t.status === 'pending')
      )
      
      if (runningTask) {
        // * Update store with current status
        updateTaskProgress(projectId, runningTask.progress || 0, runningTask.current_step)
        updateTaskStatus(projectId, runningTask.status)
      }
    } catch (error) {
      console.error('Error checking parsing status:', error)
    }
  }

  const handleStartCollection = async () => {
    if (!project) return
    
    setCollectionModalVisible(true)
    setCollectionProgress({
      status: 'running',
      progress: 0,
      queries: [],
      regions: [],
      queries_count: 0,
      regions_count: 0,
    })
    
    try {
      // * Simulate progress
      setCollectionProgress((prev) => ({ ...prev, progress: 25 }))
      
      const result = await api.startCollection(project.crm_id)
      
      setCollectionProgress({
        status: 'completed',
        progress: 100,
        queries: result.queries || [],
        regions: result.regions || [],
        queries_count: result.queries_count || 0,
        regions_count: result.regions_count || 0,
        message: result.message || 'Синхронизация завершена успешно',
      })
      
      message.success('Синхронизация данных запущена успешно')
    } catch (error: any) {
      console.error('Start collection error:', error)
      setCollectionProgress((prev) => ({
        ...prev,
        status: 'error',
        message: error.response?.data?.detail || 'Ошибка запуска синхронизации',
      }))
      message.error('Ошибка запуска синхронизации данных')
    }
  }

  // * Actions menu items
  const actionMenuItems: MenuProps['items'] = [
    {
      key: 'generate-config',
      icon: <RobotOutlined />,
      label: 'Сгенерировать AI конфиг',
      onClick: onGenerateConfig,
    },
    {
      key: 'sync',
      icon: <SyncOutlined />,
      label: 'Синхронизировать с VDL',
      onClick: onSync,
    },
    {
      type: 'divider',
    },
    {
      key: 'reanalyze',
      icon: <ReloadOutlined />,
      label: 'Переанализировать все домены',
      onClick: onReanalyze,
    },
  ]

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        padding: '16px 20px',
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e8e8e8',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {/* * Left side: Back button and project info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate('/projects')}
            style={{ 
              height: '32px',
              borderRadius: 6,
            }}
          >
            К проектам
          </Button>

          <div style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Title level={4} style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
              {project?.project_name || 'Загрузка...'}
            </Title>
            {project && (
              <>
                <Tag 
                  color={statusColors[project.status] || 'default'}
                  style={{ margin: 0, fontSize: '11px', padding: '2px 8px' }}
                >
                  {statusLabels[project.status] || project.status}
                </Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  CRM ID: {project.crm_id}
                </Text>
              </>
            )}
          </div>
        </div>

        {/* * Right side: Action buttons */}
        <Space size="small">
          {/* * Swipe mode and External links */}
          {project && (
            <>
              <Button
                type="primary"
                icon={<SwitcherOutlined />}
                onClick={() => navigate(`/projects/${projectId}/sources/swipe`)}
                style={{
                  height: '32px',
                  borderRadius: 6,
                }}
              >
                Режим проверки
              </Button>
              <Button
                icon={<LinkOutlined />}
                onClick={() => window.open(`https://victory-crm.ru/projects/${project.crm_id}`, '_blank')}
                style={{
                  height: '32px',
                  borderRadius: 6,
                }}
              >
                CRM
              </Button>
              <Button
                icon={<LinkOutlined />}
                onClick={() => window.open(`https://vdl.victory-crm.ru/project/${project.crm_id}/info`, '_blank')}
                style={{
                  height: '32px',
                  borderRadius: 6,
                }}
              >
                VDL
              </Button>
            </>
          )}

          {/* * Primary actions: Parsing buttons (only for projects with parse_manual) */}
          {project?.parse_manual && (
            <>
              {/* * Yandex Parsing Button with Active Status */}
              {activeTask && (activeTask.status === 'running' || activeTask.status === 'pending') ? (
                <Tooltip 
                  title={
                    <div>
                      <div>Парсинг в процессе...</div>
                      <div>{activeTask.current_step || `Прогресс: ${Math.round(activeTask.progress)}%`}</div>
                    </div>
                  }
                >
                  <Badge 
                    count={`${Math.round(activeTask.progress)}%`}
                    style={{ backgroundColor: '#1890ff' }}
                  >
                    <Button
                      type="primary"
                      icon={<LoadingOutlined spin />}
                      onClick={() => setParsingModalVisible(true)}
                      style={{
                        height: '32px',
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        animation: 'pulse 2s infinite',
                      }}
                    >
                      Парсинг...
                    </Button>
                  </Badge>
                </Tooltip>
              ) : activeTask && activeTask.status === 'completed' ? (
                <Tooltip title="Парсинг завершен. Нажмите для обработки результатов">
                  <Badge dot color="green">
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => setParsingModalVisible(true)}
                      style={{
                        height: '32px',
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                        border: 'none',
                      }}
                    >
                      Результаты готовы
                    </Button>
                  </Badge>
                </Tooltip>
              ) : activeTask && activeTask.status === 'uploaded' ? (
                <Tooltip title="Результаты загружены в VDL">
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    onClick={() => setParsingModalVisible(true)}
                    loading={loading}
                    style={{
                      height: '32px',
                      borderRadius: 6,
                    }}
                  >
                    Загрузка источников
                  </Button>
                </Tooltip>
              ) : activeTask && activeTask.status === 'failed' ? (
                <Tooltip title={`Ошибка: ${activeTask.error_message || 'Неизвестная ошибка'}`}>
                  <Badge dot color="red">
                    <Button
                      type="primary"
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => setParsingModalVisible(true)}
                      style={{
                        height: '32px',
                        borderRadius: 6,
                      }}
                    >
                      Ошибка парсинга
                    </Button>
                  </Badge>
                </Tooltip>
              ) : (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => setParsingModalVisible(true)}
                  loading={loading}
                  style={{
                    height: '32px',
                    borderRadius: 6,
                  }}
                >
                  Загрузка источников
                </Button>
              )}
              
              <Button
                icon={<SyncOutlined />}
                onClick={handleStartCollection}
                loading={loading}
                style={{
                  height: '32px',
                  borderRadius: 6,
                }}
              >
                Синхронизация
              </Button>
            </>
          )}

          {/* * More actions dropdown */}
          <Dropdown menu={{ items: actionMenuItems }} placement="bottomRight">
            <Button
              style={{
                height: '32px',
                borderRadius: 6,
              }}
            >
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/* * Parsing toggles warning banner */}
      {project && (project.status === 'pause' || !project.parse_phones || !project.parse_domains) && (
        <Alert
          type={project.status === 'pause' ? 'error' : 'warning'}
          showIcon
          icon={project.status === 'pause' ? <ExclamationCircleOutlined /> : <WarningOutlined />}
          message={
            project.status === 'pause'
              ? 'Проект на паузе - парсинг источников не работает'
              : 'Парсинг частично отключен'
          }
          description={
            <div style={{ fontSize: 13 }}>
              {project.status === 'pause' ? (
                <span>
                  Пока проект на паузе, тоглы «Парсинг номеров» и «Сбор исходов» в VDL отключены.
                  Источники, одобренные в свайпере сейчас, не получат флаги парсинга.
                  После возобновления нужно вручную включить парсинг для каждого источника в VDL.
                </span>
              ) : (
                <span>
                  {!project.parse_phones && 'Парсинг номеров выключен. '}
                  {!project.parse_domains && 'Сбор исходов выключен. '}
                  Новые источники не будут парситься.{' '}
                  Включите тоглы в VDL перед одобрением источников.
                </span>
              )}
              {' '}
              <a
                href={`https://victory-crm.ru/projects/${project.crm_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть в VDL
              </a>
            </div>
          }
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      {/* * Unified Yandex Parsing Modal */}
      <UnifiedParsingModal
        visible={parsingModalVisible}
        projectId={project?.id || null}
        projectCrmId={project?.crm_id || null}
        projectName={project?.project_name || null}
        onClose={() => {
          setParsingModalVisible(false)
          if (onSync) onSync()
        }}
      />
      
      {/* * CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }
        `}
      </style>

      {/* * VDL Synchronization Modal */}
      <Modal
        title={`Синхронизация данных из VDL: ${project?.project_name || ''}`}
        open={collectionModalVisible}
        onCancel={() => {
          setCollectionModalVisible(false)
          setCollectionProgress({
            status: 'idle',
            progress: 0,
            queries: [],
            regions: [],
            queries_count: 0,
            regions_count: 0,
          })
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setCollectionModalVisible(false)
              setCollectionProgress({
                status: 'idle',
                progress: 0,
                queries: [],
                regions: [],
                queries_count: 0,
                regions_count: 0,
              })
            }}
          >
            Закрыть
          </Button>,
        ]}
        width={800}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {collectionProgress.status === 'running' && (
            <div>
              <Progress percent={collectionProgress.progress} status="active" />
              <div style={{ marginTop: 8, textAlign: 'center', color: '#8c8c8c' }}>
                <Spin size="small" /> Получение данных из VDL API...
              </div>
            </div>
          )}

          {collectionProgress.status === 'completed' && (
            <div>
              <Progress percent={collectionProgress.progress} status="success" />
              <div style={{ marginTop: 8, textAlign: 'center', color: '#52c41a' }}>
                ✅ {collectionProgress.message || 'Синхронизация завершена успешно'}
              </div>
            </div>
          )}

          {collectionProgress.status === 'error' && (
            <div>
              <Progress percent={collectionProgress.progress} status="exception" />
              <div style={{ marginTop: 8, textAlign: 'center', color: '#ff4d4f' }}>
                ❌ {collectionProgress.message || 'Ошибка при выполнении синхронизации'}
              </div>
            </div>
          )}

          {collectionProgress.status !== 'idle' && (
            <div>
              <Typography.Title level={5}>Регионы ({collectionProgress.regions_count})</Typography.Title>
              {collectionProgress.regions.length > 0 ? (
                <List
                  size="small"
                  bordered
                  dataSource={collectionProgress.regions}
                  renderItem={(item) => <List.Item>{item.name}</List.Item>}
                  style={{ maxHeight: 200, overflowY: 'auto' }}
                />
              ) : (
                <div style={{ color: '#8c8c8c', padding: '8px 0' }}>Регионы не найдены</div>
              )}

              <Typography.Title level={5} style={{ marginTop: 16 }}>
                Запросы ({collectionProgress.queries_count})
              </Typography.Title>
              {collectionProgress.queries.length > 0 ? (
                <List
                  size="small"
                  bordered
                  dataSource={collectionProgress.queries}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                  style={{ maxHeight: 200, overflowY: 'auto' }}
                />
              ) : (
                <div style={{ color: '#8c8c8c', padding: '8px 0' }}>Запросы не найдены</div>
              )}
            </div>
          )}
        </Space>
      </Modal>
    </>
  )
}

export default ProjectHeader

